import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router'
import { api, ApiError, post } from '../lib/api'
import type { CompanyHit, Holding, ImportPreview, ImportRow } from '../lib/types'
import { Button, Card, ChangeText, EmptyState, LiveNumber, Skeleton, SymbolPicker, useToast } from '../ui'
import { useLiveStocks, useLiveSymbols } from '../lib/liveMarket'
import { useConfig } from '../lib/config'
import { Page } from './Page'
import { RiskRadarCard } from './RiskRadar'
import styles from './p6.module.css'

type Source = 'csv' | 'cas' | 'dhan'
const SOURCE_LABEL: Record<string, string> = { csv: 'CSV', cas_cdsl: 'CDSL CAS', cas_nsdl: 'NSDL CAS', cas: 'CAS', dhan: 'Dhan', manual: 'Manual' }
const qty = (n: number | null) => n == null ? '—' : n.toLocaleString('en-IN', { maximumFractionDigits: 3 })
const rupees = (n: number | null) => n == null ? '—' : `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
const shortDate = (d: string | null) => d ? new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${String(d).slice(0, 10)}T00:00:00Z`)) : '—'

function readFile(file: File, as: 'text' | 'base64'): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onerror = () => reject(new Error('Could not read the file'))
    r.onload = () => resolve(as === 'text' ? String(r.result) : String(r.result).replace(/^data:[^,]*,/, ''))
    if (as === 'text') r.readAsText(file); else r.readAsDataURL(file)
  })
}

function ImportDialog({ onClose, onSaved, hasHoldings }: { onClose: () => void; onSaved: () => void; hasHoldings: boolean }) {
  const toast = useToast()
  const [source, setSource] = useState<Source>('csv')
  const [file, setFile] = useState<File | null>(null)
  const [csvText, setCsvText] = useState('')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [include, setInclude] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState<'merge' | 'replace'>(hasHoldings ? 'merge' : 'replace')
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    dialogRef.current?.querySelector<HTMLElement>('button, input')?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); prev?.focus() }
  }, [onClose])

  const runPreview = async (e: FormEvent) => {
    e.preventDefault()
    setError(null); setBusy(true)
    try {
      let body: Record<string, string>
      if (source === 'csv') body = { source, csv: file ? await readFile(file, 'text') : csvText }
      else if (source === 'cas') {
        if (!file) throw new Error('Choose your CAS PDF')
        if (file.size > 10 * 1024 * 1024) throw new Error('PDF is larger than 10 MB')
        body = { source, pdf_base64: await readFile(file, 'base64'), ...(password ? { password } : {}) }
      } else body = { source, access_token: token.trim() }
      const r = await post<ImportPreview>('/holdings/import/preview', body)
      setPreview(r)
      setInclude(new Set(r.matched.map(m => m.symbol!)))
      setPassword(''); setToken('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally { setBusy(false) }
  }

  const save = async () => {
    if (!preview) return
    const rows = preview.matched.filter(m => include.has(m.symbol!)).map(m => ({ symbol: m.symbol!, quantity: m.quantity!, avg_price: m.avg_price }))
    if (!rows.length) { setError('Select at least one holding'); return }
    setBusy(true); setError(null)
    try {
      await post('/holdings/import/commit', { source: preview.source, mode, rows, skipped: preview.unmatched.length })
      toast.show({ tone: 'success', message: `${rows.length} holding${rows.length === 1 ? '' : 's'} saved` })
      onSaved()
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed') } finally { setBusy(false) }
  }

  const toggle = (s: string) => setInclude(cur => { const n = new Set(cur); if (n.has(s)) n.delete(s); else n.add(s); return n })

  return (
    <div className={styles.dialogOverlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="import-title" ref={dialogRef}>
        <div className={styles.dialogHead}>
          <h2 id="import-title">{preview ? 'Check before saving' : 'Import holdings'}</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
        {!preview ? (
          <form onSubmit={runPreview} className={styles.form}>
            <div className={styles.dialogBody}>
              <div role="group" aria-label="Import from" className={styles.seg}>
                {([['csv', 'CSV file'], ['cas', 'CDSL / NSDL CAS (PDF)'], ['dhan', 'Dhan account']] as const).map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={source === k} className={source === k ? styles.segOn : styles.segBtn}
                    onClick={() => { setSource(k); setFile(null); setError(null) }}>{l}</button>
                ))}
              </div>
              {source === 'csv' && <>
                <p className={styles.text2}>Upload your broker's holdings export (Zerodha, Groww, Upstox, Angel and others work) or our template with columns <code>symbol,quantity,avg_price</code>. <a href="/api/v2/holdings/template.csv" download>Download template</a></p>
                <label className={styles.field}><span>CSV file</span><input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] ?? null)} /></label>
                {!file && <label className={styles.field}><span>…or paste CSV</span><textarea className={styles.textarea} value={csvText} onChange={e => setCsvText(e.target.value)} placeholder={'symbol,quantity,avg_price\nRELIANCE,10,2450'} /></label>}
              </>}
              {source === 'cas' && <>
                <p className={styles.text2}>Your monthly Consolidated Account Statement from CDSL or NSDL (usually emailed; the password is often your PAN in capitals). We read equity holdings across all your demat accounts.</p>
                <label className={styles.field}><span>CAS PDF</span><input type="file" accept="application/pdf,.pdf" onChange={e => setFile(e.target.files?.[0] ?? null)} /></label>
                <label className={styles.field}><span>PDF password</span><input className={styles.input} type="password" autoComplete="off" value={password} onChange={e => setPassword(e.target.value)} /></label>
                <p className={styles.privacy}>The PDF and password are read in memory to build the preview and then discarded. We never store the file or the password.</p>
              </>}
              {source === 'dhan' && <>
                <p className={styles.text2}>Create an access token in Dhan (web.dhan.co → My Profile → Access DhanHQ APIs) and paste it here. We read your holdings once.</p>
                <label className={styles.field}><span>Dhan access token</span><input className={styles.input} type="password" autoComplete="off" value={token} onChange={e => setToken(e.target.value)} /></label>
                <p className={styles.privacy}>The token is used for one read-only request and not stored. You can revoke it in Dhan at any time.</p>
              </>}
              {error && <p className={styles.error} role="alert">{error}</p>}
            </div>
            <div className={styles.dialogFoot}>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="primary" loading={busy}
                disabled={busy || (source === 'csv' && !file && !csvText.trim()) || (source === 'cas' && !file) || (source === 'dhan' && token.trim().length < 20)}>Preview</Button>
            </div>
          </form>
        ) : (
          <>
            <div className={styles.dialogBody}>
              <p className={styles.text2}>From <strong>{SOURCE_LABEL[preview.source] ?? preview.source}</strong>: {preview.matched.length} stock{preview.matched.length === 1 ? '' : 's'} recognised
                {preview.unmatched.length ? `, ${preview.unmatched.length} not recognised` : ''}{preview.skipped_non_equity ? `, ${preview.skipped_non_equity} mutual fund / bond line${preview.skipped_non_equity === 1 ? '' : 's'} skipped` : ''}.</p>
              {preview.matched.length > 0 && (
                <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Holdings to save (scrolls sideways)">
                  <table className={styles.table}>
                    <caption className="sr-only">Holdings to save</caption>
                    <thead><tr><th scope="col">Save</th><th scope="col">Stock</th><th scope="col" className={styles.th_num}>Quantity</th><th scope="col" className={styles.th_num}>Avg price</th></tr></thead>
                    <tbody>
                      {preview.matched.map(m => (
                        <tr key={m.symbol}>
                          <td><input type="checkbox" aria-label={`Save ${m.symbol}`} checked={include.has(m.symbol!)} onChange={() => toggle(m.symbol!)} /></td>
                          <td><span className={styles.sym}>{m.symbol}</span><span className={styles.name}>{m.name}</span></td>
                          <td className={styles.num}>{qty(m.quantity)}</td>
                          <td className={styles.num}>{rupees(m.avg_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview.unmatched.length > 0 && (
                <details>
                  <summary className={styles.text2}>Not recognised ({preview.unmatched.length}) — not saved</summary>
                  <ul className={styles.text2}>{preview.unmatched.slice(0, 50).map((u: ImportRow, i) => <li key={i}>{u.symbol || u.isin || u.name} {u.status === 'invalid_quantity' ? '(quantity missing)' : ''}</li>)}</ul>
                </details>
              )}
              <fieldset className={styles.fieldset}>
                <legend>How to save</legend>
                <label className={styles.check}><input type="radio" name="mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />Replace my holdings with this list</label>
                <label className={styles.check}><input type="radio" name="mode" checked={mode === 'merge'} onChange={() => setMode('merge')} />Add to / update my current holdings</label>
              </fieldset>
              {error && <p className={styles.error} role="alert">{error}</p>}
            </div>
            <div className={styles.dialogFoot}>
              <Button onClick={() => setPreview(null)}>Back</Button>
              <Button variant="primary" onClick={save} loading={busy} disabled={busy || include.size === 0}>Save {include.size} holding{include.size === 1 ? '' : 's'}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function PortfolioPage() {
  const toast = useToast()
  const [data, setData] = useState<{ data: Holding[]; last_import: { source: string; rows_saved: number; created_at: string } | null; prices_connected?: boolean } | null>(null)
  const cfg = useConfig()
  const live = useLiveStocks()
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [adding, setAdding] = useState<CompanyHit | null>(null)
  const [addQty, setAddQty] = useState('')
  const [addAvg, setAddAvg] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [editQty, setEditQty] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  const load = useCallback(() => {
    api<typeof data & object>('/holdings').then(r => { setData(r); setError(null) }).catch(e => setError((e as Error).message))
  }, [])
  useEffect(load, [load, refreshKey])

  const saveHolding = async (symbol: string, quantity: number, avg: number | null) => {
    try {
      await api(`/holdings/${encodeURIComponent(symbol)}`, { method: 'PUT', body: JSON.stringify({ quantity, avg_price: avg }) })
      toast.show({ tone: 'success', message: `${symbol} saved` })
      setAdding(null); setAddQty(''); setAddAvg(''); setEditing(null)
      setRefreshKey(k => k + 1)
    } catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not save' }) }
  }
  const remove = async (symbol: string) => {
    try { await api(`/holdings/${encodeURIComponent(symbol)}`, { method: 'DELETE' }); setRefreshKey(k => k + 1) }
    catch { toast.show({ tone: 'error', message: `Could not remove ${symbol}` }) }
  }

  // Live overlay: every price below ticks with /market/stream; REST gives the first value.
  const holdings = (data?.data ?? []).map(h => {
    const l = live(h.symbol)
    const ltp = l?.ltp ?? h.ltp ?? null
    const change = l?.change ?? h.change ?? null
    const value = ltp != null ? ltp * h.quantity : null
    const cost = h.avg_price != null ? h.avg_price * h.quantity : null
    return { ...h, ltp, change, change_pct: l?.change_pct ?? h.change_pct ?? null, value, cost, pnl: value != null && cost != null ? value - cost : null, day: change != null ? change * h.quantity : null }
  })
  useLiveSymbols(holdings.slice(0, 20).map(h => h.symbol))
  const hasHoldings = holdings.length > 0
  const priced = holdings.filter(h => h.value != null)
  const sum = (xs: Array<number | null>) => xs.reduce<number>((a, x) => a + (x ?? 0), 0)
  const totals = {
    value: sum(priced.map(h => h.value)), day: sum(priced.map(h => h.day)),
    cost: sum(priced.filter(h => h.cost != null).map(h => h.cost)), pnl: sum(priced.filter(h => h.cost != null).map(h => h.pnl)),
    invested: sum(holdings.map(h => h.cost)),
  }
  const showPrices = cfg.prices_enabled && priced.length > 0
  const pct = (a: number, b: number) => (b ? (a / b) * 100 : null)
  return (
    <Page title="Portfolio" subtitle="Your holdings, their filings, corporate actions and Risk Radar flags"
      actions={<><Button variant="primary" onClick={() => setImporting(true)}>Import holdings</Button></>}>
      {importing && <ImportDialog hasHoldings={hasHoldings} onClose={() => setImporting(false)} onSaved={() => { setImporting(false); setRefreshKey(k => k + 1) }} />}
      {hasHoldings && cfg.prices_enabled && (
        <section aria-label="Portfolio value" className={styles.stats} style={{ marginBottom: 16 }}>
          <div className={styles.stat}><span className={styles.statLabel}>Current value</span><LiveNumber className={styles.statValue} value={showPrices ? Math.round(totals.value) : null} decimals={0} prefix="₹" /><span className={styles.muted}>{priced.length} of {holdings.length} holdings priced live</span></div>
          <div className={styles.stat}><span className={styles.statLabel}>Today’s P&amp;L</span><span className={styles.statValue}><ChangeText absolute={showPrices ? totals.day : null} percent={showPrices ? pct(totals.day, totals.value - totals.day) : null} decimals={0} size="lg" weight="bold" /></span></div>
          <div className={styles.stat}><span className={styles.statLabel}>Overall P&amp;L</span><span className={styles.statValue}><ChangeText absolute={showPrices && totals.cost ? totals.pnl : null} percent={showPrices && totals.cost ? pct(totals.pnl, totals.cost) : null} decimals={0} size="lg" weight="bold" /></span><span className={styles.muted}>vs average buy price{totals.cost < totals.invested - 1 ? ' (priced holdings only)' : ''}</span></div>
          <div className={styles.stat}><span className={styles.statLabel}>Invested</span><span className={styles.statValue}>{rupees(Math.round(totals.invested))}</span><span className={styles.muted}>quantity × average price</span></div>
        </section>
      )}
      <div className={styles.grid}>
        <div className={styles.col}>
          <Card title={hasHoldings ? `Holdings (${holdings.length})` : 'Holdings'} variant="flush"
            action={data?.last_import ? <span className={styles.muted}>Last import: {SOURCE_LABEL[data.last_import.source] ?? data.last_import.source}, {new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }).format(new Date(data.last_import.created_at))}</span> : undefined}>
            {error ? <EmptyState kind="error" title="Could not load holdings" compact>{error}</EmptyState>
              : !data ? <div className={styles.pad}><Skeleton variant="row" lines={4} label="Loading holdings" /></div>
              : !hasHoldings ? (
                <EmptyState title="No holdings yet" action={<Button variant="primary" onClick={() => setImporting(true)}>Import holdings</Button>}>
                  Import a broker CSV, your CDSL/NSDL CAS statement or your Dhan account — or add stocks one by one below. You'll see filings, corporate actions and Risk Radar flags for what you own.
                </EmptyState>
              ) : (
                <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Your holdings (scrolls sideways)">
                  <table className={styles.table}>
                    <caption className="sr-only">Your holdings</caption>
                    <thead><tr>
                      <th scope="col">Stock</th><th scope="col" className={styles.th_num}>Qty</th><th scope="col" className={styles.th_num}>Avg price</th>
                      {showPrices && <><th scope="col" className={styles.th_num}>LTP</th><th scope="col" className={styles.th_num}>Day</th><th scope="col" className={styles.th_num}>Value</th><th scope="col" className={styles.th_num}>P&amp;L</th></>}
                      <th scope="col" className={styles.th_num}>Filings 7d</th><th scope="col">Risk</th><th scope="col">Next action</th><th scope="col"><span className="sr-only">Actions</span></th>
                    </tr></thead>
                    <tbody>
                      {holdings.map(h => (
                        <tr key={h.symbol}>
                          <td><Link to={`/stock/${encodeURIComponent(h.symbol)}`} className={styles.sym}>{h.symbol}</Link><span className={styles.name}>{h.name}</span></td>
                          <td className={styles.num}>
                            {editing === h.symbol ? (
                              <form className={styles.row} style={{ justifyContent: 'flex-end' }} onSubmit={e => { e.preventDefault(); const q = Number(editQty); if (q > 0) void saveHolding(h.symbol, q, h.avg_price) }}>
                                <label className="sr-only" htmlFor={`q-${h.symbol}`}>Quantity for {h.symbol}</label>
                                <input id={`q-${h.symbol}`} className={styles.input} style={{ width: 90 }} type="number" min="0.001" step="any" value={editQty} onChange={e => setEditQty(e.target.value)} autoFocus />
                                <Button size="sm" type="submit">Save</Button>
                              </form>
                            ) : qty(h.quantity)}
                          </td>
                          <td className={styles.num}>{rupees(h.avg_price)}</td>
                          {showPrices && <>
                            <td className={styles.num}><LiveNumber value={h.ltp} /></td>
                            <td className={styles.num}><ChangeText percent={h.change_pct} size="sm" /></td>
                            <td className={styles.num}>{h.value == null ? '—' : rupees(Math.round(h.value))}</td>
                            <td className={styles.num}>{h.pnl == null ? '—' : <ChangeText absolute={h.pnl} percent={h.cost ? pct(h.pnl, h.cost) : null} decimals={0} size="sm" />}</td>
                          </>}
                          <td className={styles.num}>{h.filings_7d > 0 ? <Link to={`/stock/${encodeURIComponent(h.symbol)}?tab=filings`}>{h.filings_7d}</Link> : '0'}</td>
                          <td>{h.risks_90d > 0 ? <span className={`${styles.pill} ${styles.pillDown}`}>{h.risks_90d} flag{h.risks_90d === 1 ? '' : 's'}</span> : <span className={styles.muted}>—</span>}</td>
                          <td className={styles.num} style={{ textAlign: 'left' }}>{shortDate(h.next_action)}</td>
                          <td>
                            <div className={styles.row} style={{ flexWrap: 'nowrap' }}>
                              <button type="button" className={styles.iconBtn} aria-label={`Edit ${h.symbol} quantity`} onClick={() => { setEditing(h.symbol); setEditQty(String(h.quantity)) }}>✎</button>
                              <button type="button" className={styles.iconBtn} aria-label={`Remove ${h.symbol}`} onClick={() => remove(h.symbol)}>✕</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
          <Card title="Add a stock">
            <div className={styles.form}>
              {!adding ? <SymbolPicker label="Stock to add to holdings" onSelect={setAdding} />
                : (
                  <form className={styles.row} onSubmit={e => { e.preventDefault(); const q = Number(addQty); if (q > 0) void saveHolding(adding.symbol, q, addAvg ? Number(addAvg) : null) }}>
                    <span className={styles.sym}>{adding.symbol}</span>
                    <label className={styles.field}><span>Quantity</span><input className={styles.input} type="number" min="0.001" step="any" required value={addQty} onChange={e => setAddQty(e.target.value)} autoFocus /></label>
                    <label className={styles.field}><span>Avg price (optional)</span><input className={styles.input} type="number" min="0" step="any" value={addAvg} onChange={e => setAddAvg(e.target.value)} /></label>
                    <div className={styles.row} style={{ alignSelf: 'flex-end' }}><Button type="submit" variant="primary">Save</Button><Button onClick={() => setAdding(null)}>Cancel</Button></div>
                  </form>
                )}
              {!cfg.prices_enabled && <p className={styles.muted}>Current value and P&amp;L need live prices: market data not connected.</p>}
            </div>
          </Card>
        </div>
        <div className={styles.col}>
          <RiskRadarCard key={`r${refreshKey}`} scope="holdings" title="Risk Radar · your holdings" emptyHint={hasHoldings ? 'No flagged filings for your holdings in the last 90 days.' : 'Import holdings to see flags for the stocks you own.'} />
          <Card title="Upcoming actions" action={<Link to="/calendar?mine=1&view=list">Calendar</Link>}>
            <p className={styles.text2}>Ex-dates, record dates and board meetings for your holdings are in the calendar. <Link to="/calendar?mine=1">Show my holdings only</Link></p>
          </Card>
        </div>
      </div>
    </Page>
  )
}
